import { useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { usePageTitle } from '../hooks/usePageTitle'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { contactsApi, organizationsApi, type Contact, type Organization } from '../api/contacts'
import { Pagination } from '../components/ui/Pagination'
import { usePersistedPageSize } from '../hooks/usePersistedPageSize'
import { Modal } from '../components/ui/Modal'
import { PageSpinner } from '../components/ui/Spinner'
import { EmptyState } from '../components/ui/EmptyState'
import { formatDate, fullName, maskEmail } from '../lib/utils'
import { Plus, Users, Search } from 'lucide-react'
import { useForm } from 'react-hook-form'
import toast from 'react-hot-toast'
import { useAuthStore } from '../store/auth'

export function ContactsListPage() {
  usePageTitle('Contacts')
  const { companyId } = useParams<{ companyId: string }>()
  const { user } = useAuthStore()
  const isSalesUser = user?.role === 'sales_user'
  const canSeePhone = !isSalesUser || !!user?.canSeePhone
  const canSeeEmail = !isSalesUser || !!user?.canSeeEmail
  const navigate = useNavigate()
  const qc = useQueryClient()
  const [page, setPage] = useState(1)
  const [limit, setLimit] = usePersistedPageSize('contacts', companyId)
  const [showCreate, setShowCreate] = useState(false)
  const [search, setSearch] = useState('')

  const { data, isLoading } = useQuery({
    queryKey: ['contacts', companyId, page, limit, search],
    queryFn: () => contactsApi.list(companyId!, { page, limit, search: search || undefined }).then(r => r.data),
    enabled: !!companyId,
  })

  const { data: orgs } = useQuery({
    queryKey: ['organizations', companyId],
    queryFn: () => organizationsApi.list(companyId!, { limit: 500 }).then(r => r.data.data),
    enabled: !!companyId,
  })
  const orgMap = new Map((orgs ?? []).map((o: Organization) => [o.id, o.name]))

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-white">
        <h1 className="text-base font-semibold text-gray-900">Contacts</h1>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 pointer-events-none" />
            <input
              value={search}
              onChange={e => { setSearch(e.target.value); setPage(1) }}
              placeholder="Search contacts..."
              className="input pl-8 h-8 text-sm w-48"
            />
          </div>
          <button onClick={() => setShowCreate(true)} className="btn btn-primary btn-sm">
            <Plus className="w-3.5 h-3.5" /> New Contact
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-auto bg-white">
        {isLoading ? <PageSpinner /> : (
          <table className="table">
            <thead>
              <tr><th>Name</th><th>Email</th><th>Phone</th><th>Job Title</th><th>Campaign</th><th>Created</th></tr>
            </thead>
            <tbody>
              {data?.data.length === 0 && (
                <tr><td colSpan={5}>
                  <EmptyState icon={<Users className="w-10 h-10" />} title="No contacts" />
                </td></tr>
              )}
              {data?.data.map((c: Contact) => (
                <tr key={c.id} className="cursor-pointer" onClick={() => navigate(`/company/${companyId}/contacts/${c.id}`)}>
                  <td className="font-medium">{fullName(c.firstName, c.lastName)}</td>
                  <td className="text-gray-600">
                    {canSeeEmail ? (c.email ?? '—') : c.email ? maskEmail(c.email) : '—'}
                  </td>
                  <td className="text-gray-600">
                    {canSeePhone
                      ? (c.phone ?? '—')
                      : isSalesUser && c.phone
                        ? (c.phone.length <= 6
                            ? '•'.repeat(c.phone.length)
                            : c.phone.slice(0, 4) + '•'.repeat(c.phone.length - 6) + c.phone.slice(-2))
                        : '—'}
                  </td>
                  <td className="text-gray-600">{c.jobTitle ?? '—'}</td>
                  <td>
                    {c.organizationId && orgMap.has(c.organizationId) && (
                      <span className="badge badge-purple text-xs">{orgMap.get(c.organizationId)}</span>
                    )}
                  </td>
                  <td className="text-gray-500 text-xs">{formatDate(c.createdAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {data && data.meta.total > 0 && (
        <Pagination
          page={data.meta.page}
          totalPages={data.meta.totalPages}
          total={data.meta.total}
          limit={limit}
          onPageChange={setPage}
          onPageSizeChange={size => { setLimit(size); setPage(1) }}
        />
      )}

      <CreateContactModal
        open={showCreate}
        onClose={() => setShowCreate(false)}
        companyId={companyId!}
        onSuccess={() => { setShowCreate(false); qc.invalidateQueries({ queryKey: ['contacts', companyId] }) }}
      />
    </div>
  )
}

function CreateContactModal({ open, onClose, companyId, onSuccess }: {
  open: boolean; onClose: () => void; companyId: string; onSuccess: () => void
}) {
  const { register, handleSubmit, reset } = useForm<{ firstName: string; lastName: string; email?: string; phone?: string; jobTitle?: string }>()
  const mutation = useMutation({
    mutationFn: (dto: { firstName: string; lastName: string; email?: string; phone?: string; jobTitle?: string }) =>
      contactsApi.create(companyId, dto),
    onSuccess: () => { reset(); toast.success('Contact created'); onSuccess() },
  })
  return (
    <Modal open={open} onClose={onClose} title="New Contact" size="sm">
      <form onSubmit={handleSubmit(d => mutation.mutate(d))} className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <div><label className="label">First Name *</label><input {...register('firstName', { required: true })} className="input" /></div>
          <div><label className="label">Last Name *</label><input {...register('lastName', { required: true })} className="input" /></div>
        </div>
        <div><label className="label">Email</label><input {...register('email')} type="email" className="input" /></div>
        <div><label className="label">Phone</label><input {...register('phone')} className="input" /></div>
        <div><label className="label">Job Title</label><input {...register('jobTitle')} className="input" /></div>
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className="btn btn-secondary btn-sm">Cancel</button>
          <button type="submit" disabled={mutation.isPending} className="btn btn-primary btn-sm">Create</button>
        </div>
      </form>
    </Modal>
  )
}
